import type { Node as ProseMirrorNode } from "prosemirror-model";
import {
  Plugin,
  PluginKey,
  TextSelection,
  type EditorState,
  type Transaction,
} from "prosemirror-state";
import { Decoration, DecorationSet, type EditorView } from "prosemirror-view";

import { findParentOfType, nodes } from "../schema";
import { isToggleOpen, setToggleOpen, toggleKey } from "../toggle-state";
import type { FindMatch, FindOptions } from "../types";

export interface FindState extends FindOptions {
  open: boolean;
  query: string;
  matches: FindMatch[];
  /** Index into `matches`, or -1 when there is nothing to step through. */
  index: number;
}

export const findKey = new PluginKey<FindState>("wikiFind");

const EMPTY: FindState = {
  open: false,
  query: "",
  caseSensitive: false,
  regex: false,
  matches: [],
  index: -1,
};

interface FindAction {
  type: "open" | "close" | "params" | "step" | "index";
  query?: string;
  caseSensitive?: boolean;
  regex?: boolean;
  step?: 1 | -1;
  index?: number;
}

export function escapeForRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function buildRegExp(state: FindState): RegExp | null {
  if (state.query === "") return null;
  const source = state.regex ? state.query : escapeForRegExp(state.query);
  const flags = state.caseSensitive ? "g" : "gi";
  try {
    return new RegExp(source, flags);
  } catch {
    // An incomplete regular expression is a normal intermediate state while
    // the user is typing one; it simply matches nothing.
    return null;
  }
}

/**
 * Matching runs **per text block** (`editor.md` §11, open question 8): a search
 * term never spans a paragraph boundary, which is what a reader expects and
 * what keeps every match mappable to a single decoration.
 */
export function findMatches(doc: ProseMirrorNode, state: FindState): FindMatch[] {
  const pattern = buildRegExp(state);
  if (!pattern) return [];
  const matches: FindMatch[] = [];
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    // Every inline leaf contributes exactly one character, so an offset in
    // this string is an offset in the block.
    const text = node.textBetween(0, node.content.size, undefined, "￼");
    pattern.lastIndex = 0;
    let found = pattern.exec(text);
    while (found !== null) {
      if (found[0] === "") {
        pattern.lastIndex += 1;
      } else {
        matches.push({ from: pos + 1 + found.index, to: pos + 1 + found.index + found[0].length });
      }
      found = pattern.exec(text);
    }
    return false;
  });
  return matches;
}

function clampIndex(matches: FindMatch[], index: number): number {
  if (matches.length === 0) return -1;
  return ((index % matches.length) + matches.length) % matches.length;
}

export function findPlugin(): Plugin<FindState> {
  return new Plugin<FindState>({
    key: findKey,
    state: {
      init: () => EMPTY,
      apply(tr, value, _old, newState) {
        const action = tr.getMeta(findKey) as FindAction | undefined;
        let next = value;
        if (action?.type === "close") return EMPTY;
        if (action?.type === "open") next = { ...value, open: true };
        if (action?.type === "params") {
          next = {
            ...next,
            open: true,
            query: action.query ?? next.query,
            caseSensitive: action.caseSensitive ?? next.caseSensitive,
            regex: action.regex ?? next.regex,
          };
          const matches = findMatches(newState.doc, next);
          return { ...next, matches, index: matches.length === 0 ? -1 : 0 };
        }
        if (action?.type === "step") {
          return { ...next, index: clampIndex(next.matches, next.index + (action.step ?? 1)) };
        }
        if (action?.type === "index") {
          return { ...next, index: clampIndex(next.matches, action.index ?? 0) };
        }
        if (tr.docChanged && next.query !== "") {
          const matches = findMatches(newState.doc, next);
          return { ...next, matches, index: clampIndex(matches, Math.max(next.index, 0)) };
        }
        return next;
      },
    },
    props: {
      decorations(state) {
        const value = findKey.getState(state);
        if (!value || value.matches.length === 0) return DecorationSet.empty;
        return DecorationSet.create(
          state.doc,
          value.matches.map((match, index) =>
            Decoration.inline(match.from, match.to, {
              class:
                index === value.index
                  ? "wiki-find-match wiki-find-match--current"
                  : "wiki-find-match",
            }),
          ),
        );
      },
    },
  });
}

export function getFindState(state: EditorState): FindState {
  return findKey.getState(state) ?? EMPTY;
}

function dispatchFind(view: EditorView, action: FindAction): void {
  view.dispatch(view.state.tr.setMeta(findKey, action));
}

export function openFind(view: EditorView): void {
  dispatchFind(view, { type: "open" });
}

export function closeFind(view: EditorView): void {
  dispatchFind(view, { type: "close" });
}

export function setFind(
  view: EditorView,
  params: Partial<FindOptions> & { query?: string },
): FindMatch[] {
  dispatchFind(view, { type: "params", ...params });
  const next = getFindState(view.state);
  revealCurrentMatch(view);
  return next.matches;
}

/** A match inside a collapsed toggle opens it, per §11. */
function revealCurrentMatch(view: EditorView): void {
  const state = getFindState(view.state);
  const match = state.matches[state.index];
  if (!match) return;
  const found = findParentOfType(view.state.doc.resolve(match.from), nodes.toggle);
  if (found) {
    const key = toggleKey(found.node);
    if (!isToggleOpen(null, key)) setToggleOpen(null, key, true);
  }
  view.dispatch(
    view.state.tr
      .setSelection(TextSelection.create(view.state.doc, match.from, match.to))
      .scrollIntoView(),
  );
}

export function stepFind(view: EditorView, step: 1 | -1): void {
  dispatchFind(view, { type: "step", step });
  revealCurrentMatch(view);
}

/** Replace the current match, then keep the cursor on the next one. */
export function replaceCurrent(view: EditorView, replacement: string): boolean {
  const state = getFindState(view.state);
  const match = state.matches[state.index];
  if (!match) return false;
  const tr: Transaction = view.state.tr;
  if (replacement === "") tr.delete(match.from, match.to);
  else tr.insertText(replacement, match.from, match.to);
  view.dispatch(tr);
  revealCurrentMatch(view);
  return true;
}

export function replaceAll(view: EditorView, replacement: string): number {
  const state = getFindState(view.state);
  if (state.matches.length === 0) return 0;
  const tr = view.state.tr;
  // Back to front, so earlier positions stay valid.
  for (const match of [...state.matches].reverse()) {
    if (replacement === "") tr.delete(match.from, match.to);
    else tr.insertText(replacement, match.from, match.to);
  }
  view.dispatch(tr);
  return state.matches.length;
}
